import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { Send, Bot, Sparkles, Camera, Image as ImgIcon } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState(null);
  const [pets, setPets] = useState([]);
  const [petId, setPetId] = useState("");
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!active) { setPets([]); setPetId(""); return; }
    api.get("/pets").then(({ data }) => {
      const filtered = data.filter(p => p.owner_id === active.other.id);
      setPets(filtered);
      setPetId(filtered[0]?.id || "");
    });
  }, [active]);

  const pickPhoto = (label) => {
    setUploadingLabel(label);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    if (file.size > 2_500_000) { toast.error("Photo too large (max 2.5 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("recipient_id", active.other.id);
    fd.append("label", uploadingLabel || "");
    if (petId) fd.append("pet_id", petId);
    try {
      await api.post("/messages/photo", fd);
      toast.success(uploadingLabel ? `${uploadingLabel[0].toUpperCase()+uploadingLabel.slice(1)} photo sent` : "Photo sent");
      loadMsgs(active.other.id);
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingLabel(null);
    }
  };

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    if (!active && data.length) setActive(data[0]);
  };
  useEffect(() => { loadThreads(); }, []);

  const loadMsgs = async (id) => {
    if (!id) return;
    const { data } = await api.get(`/messages/${id}`);
    setMsgs(data);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  useEffect(() => {
    if (!active) return;
    loadMsgs(active.other.id);
    const t = setInterval(() => loadMsgs(active.other.id), 5000);
    return () => clearInterval(t);
  }, [active]);

  const send = async () => {
    if (!body.trim() || !active) return;
    try {
      await api.post("/messages", { recipient_id: active.other.id, body: body.trim() });
      setBody("");
      loadMsgs(active.other.id);
      loadThreads();
    } catch { toast.error("Send failed"); }
  };

  const aiDraft = async () => {
    if (!active) return;
    const context = msgs.slice(-6).map(m => `${m.sender_role === "groomer" ? "Me" : active.other.name}: ${m.body}`).join("\n");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai", {
        task: "reply",
        text: `Context (last messages):\n${context}\n\nDraft my next reply.`,
      });
      setBody(data.text.trim());
      toast.success("Draft ready — tweak or send");
    } catch (e) { toast.error("AI draft failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <header>
        <div className="label-accent">Inbox</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Messages</h1>
      </header>
      <div className="grid lg:grid-cols-5 gap-6 h-[70vh]">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-[#E5DCCE] p-3 shadow-sm overflow-y-auto">
          {threads.length === 0 && <p className="text-sm text-[#6B625B] p-3">No conversations yet.</p>}
          <ul className="space-y-1" data-testid="thread-list">
            {threads.map(t => (
              <li key={t.thread_id}>
                <button onClick={() => setActive(t)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
                    active?.thread_id === t.thread_id ? "bg-[#F5E6CF]" : "hover:bg-[#F3EDE4]"
                  }`}
                  data-testid={`thread-${t.other.id}`}>
                  <div className="font-semibold text-sm">{t.other.name}</div>
                  <div className="text-xs text-[#6B625B] truncate">
                    {t.last_message?.body || "Start a conversation"}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 bg-white rounded-3xl border border-[#E5DCCE] shadow-sm flex flex-col">
          {active ? (
            <>
              <div className="border-b border-[#E5DCCE] p-4">
                <div className="font-semibold">{active.other.name}</div>
                <div className="text-xs text-[#6B625B]">{active.other.email}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-scroll">
                {msgs.map(m => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                        mine ? "bg-[#C85A17] text-white" : "bg-[#F3EDE4] text-[#1F1A17]"
                      }`}>
                        {m.photo_url && (
                          <div className="mb-2 -mx-1 -mt-1">
                            {m.photo_label && (
                              <div className={`inline-block mb-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                m.photo_label === "before"
                                  ? "bg-amber-200 text-amber-900"
                                  : "bg-emerald-200 text-emerald-900"
                              }`}>{m.photo_label}</div>
                            )}
                            <img src={`${API}${m.photo_url.replace('/api','')}`}
                              alt={m.photo_label || "attachment"}
                              className="rounded-xl max-w-full max-h-64 object-cover"/>
                          </div>
                        )}
                        {m.body && <div>{m.body}</div>}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#6B625B]"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef}/>
              </div>
              <div className="border-t border-[#E5DCCE] p-3 space-y-2">
                <input type="file" accept="image/*" capture="environment" ref={fileInputRef}
                  onChange={uploadPhoto} className="hidden" data-testid="input-photo-file"/>
                <div className="flex gap-2 flex-wrap items-center">
                  <Button size="sm" variant="outline" onClick={() => pickPhoto("before")}
                    className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-50"
                    data-testid="btn-photo-before">
                    <Camera className="w-3.5 h-3.5 mr-1"/> Before
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => pickPhoto("after")}
                    className="rounded-full border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                    data-testid="btn-photo-after">
                    <Camera className="w-3.5 h-3.5 mr-1"/> After
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => pickPhoto("")}
                    className="rounded-full border-[#E5DCCE] text-[#6B625B]"
                    data-testid="btn-photo-any">
                    <ImgIcon className="w-3.5 h-3.5 mr-1"/> Photo
                  </Button>
                  {pets.length > 0 && (
                    <select value={petId} onChange={(e) => setPetId(e.target.value)}
                      className="ml-auto text-xs bg-white border border-[#E5DCCE] rounded-full px-3 py-1.5"
                      data-testid="msg-pet-select">
                      <option value="">No pet tag</option>
                      {pets.map(p => <option key={p.id} value={p.id}>Tag: {p.name}</option>)}
                    </select>
                  )}
                </div>
                <div className="flex gap-2">
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder="Type a message…" className="min-h-[52px] resize-none"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    data-testid="msg-input"/>
                  <div className="flex flex-col gap-2">
                    <Button onClick={aiDraft} disabled={aiBusy} variant="outline"
                      className="rounded-full border-[#2D4A3E] text-[#2D4A3E]" data-testid="btn-ai-draft">
                      {aiBusy ? <Sparkles className="w-4 h-4 animate-pulse"/> : <Bot className="w-4 h-4"/>}
                    </Button>
                    <Button onClick={send} className="rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="btn-send">
                      <Send className="w-4 h-4"/>
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-content-center text-[#6B625B]">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
